import React, { useEffect } from 'react'
import styled from 'styled-components'
import { FormattedMessage } from 'react-intl'
import { MODULES_GROUPS } from '~/constants/modulesItems'
import { CustomInput, UncontrolledTooltip } from 'reactstrap'
import { useFormikContext } from 'formik'
import { ChevronDown, ChevronUp } from 'react-feather'
import { useSelector } from 'react-redux'
import { SUPER_ADMIN } from '~/constants/roles'
import _ from 'lodash'

export const ModulesTable = ({
  userId,
  userRole,
  isDisabled,
  value,
  setValue,
  noHeader = false,
}) => {
  const [groups, setGroups] = React.useState({})
  const formikContext = useFormikContext()
  const values = value || formikContext?.values
  const setFieldValue = setValue || formikContext?.setFieldValue
  const userProfile = useSelector(state => state?.user?.profile)
  const userPermission = useSelector(
    state => state?.user?.profile?.userPermission
  )
  const myId = userProfile.id
  const myRole = userProfile?.role
  const isSuperAdmin = myRole === SUPER_ADMIN

  function isModuleDisabled(permission) {
    const isMyProfile = userId === myId
    const newUser = !userId
    if (isSuperAdmin) return false
    if (isMyProfile) return true
    if (!isMyProfile && !newUser)
      return myRole < (userRole || 0) || !userPermission[permission]
    if (newUser) return !userPermission[permission]
    return false
  }

  function isGroupDisabled(group) {
    if (group.permission) return isModuleDisabled(group.permission)
    const isMyProfile = userId === myId
    if (isSuperAdmin) return false
    if (isMyProfile) return true

    const groupModules = MODULES_GROUPS.find(g => g.id === group.id)?.modules
    const hasAnyPermission = groupModules?.some(
      module => userPermission[module.permission]
    )

    return !hasAnyPermission
  }

  function handleSetFieldValue(field, newValue) {
    if (setValue) {
      setValue(prevState => {
        const newState = _.cloneDeep(prevState)
        _.set(newState, field, newValue)
        return newState
      })
    } else {
      setFieldValue(field, newValue)
    }
  }

  useEffect(() => {
    for (const group of MODULES_GROUPS) {
      if (!group.modules) continue

      const anyChecked = group.modules.some(module => {
        return values?.user?.userPermission?.[module.permission] || false
      })
      const allUnchecked = group.modules.every(module => {
        return !values?.user?.userPermission?.[module.permission] || false
      })

      if (anyChecked) {
        setGroups(prevState => ({
          ...prevState,
          [group.id]: true,
        }))
      }

      if (allUnchecked) {
        setGroups(prevState => ({
          ...prevState,
          [group.id]: false,
        }))
      }
    }
  }, [values])

  return (
    <StyledModules id="modules-table-container">
      {noHeader ? null : (
        <h2>
          <FormattedMessage id="Permissions" />
        </h2>
      )}
      <div className="modules-table">
        {MODULES_GROUPS.map(group => {
          return (
            <details key={group.id}>
              <summary>
                <div className="group-icon">{group.icon}</div>
                <div className="group-name">
                  <FormattedMessage id={group.name} />
                </div>
                <div className="group-check" id={`check-g-${group.id}`}>
                  <CustomInput
                    type="switch"
                    id={`group-${group.id}`}
                    name={`group-${group.id}`}
                    checked={
                      group.modules
                        ? groups?.[group.id]
                        : values?.user?.userPermission?.[group.permission] ||
                          false
                    }
                    onChange={() => {
                      if (group.modules) {
                        const newValue = !groups[group.id]
                        setGroups(prevState => ({
                          ...prevState,
                          [group.id]: newValue,
                        }))
                        group.modules.forEach(module => {
                          if (
                            isSuperAdmin ||
                            userPermission[module.permission]
                          ) {
                            handleSetFieldValue(
                              `user.userPermission.${module.permission}`,
                              newValue
                            )
                          }
                        })
                      } else {
                        handleSetFieldValue(
                          `user.userPermission.${group.permission}`,
                          !values?.user?.userPermission?.[group.permission] ||
                            false
                        )
                      }
                    }}
                    disabled={isGroupDisabled(group) || isDisabled}
                    inline
                  />
                </div>
                {isGroupDisabled(group) && (
                  <UncontrolledTooltip
                    container="modules-table-container"
                    target={`check-g-${group.id}`}
                    placement="top"
                  >
                    <FormattedMessage id="You don't have authorization to change this permission" />
                  </UncontrolledTooltip>
                )}
                <div className="group-chevron">
                  {group.modules && (
                    <>
                      <ChevronDown
                        className="group-closed"
                        color="rgba(47, 43, 61, 0.9)"
                        size="1.5rem"
                      />
                      <ChevronUp
                        className="group-open"
                        color="rgba(47, 43, 61, 0.9)"
                        size="1.5rem"
                      />
                    </>
                  )}
                </div>
              </summary>
              {group.modules &&
                group.modules.map(module => (
                  <div key={module.name} className="module-item">
                    <div>
                      <FormattedMessage id={module.name} />
                    </div>
                    <div id={`check-m-${module.permission}`}>
                      <CustomInput
                        type="switch"
                        id={`module-${module.name}`}
                        name={`module-${module.name}`}
                        checked={
                          values?.user?.userPermission?.[module.permission] ||
                          false
                        }
                        onChange={() =>
                          handleSetFieldValue(
                            `user.userPermission.${module.permission}`,
                            !values?.user?.userPermission?.[
                              module.permission
                            ] || false
                          )
                        }
                        disabled={
                          isModuleDisabled(module.permission) || isDisabled
                        }
                        inline
                      />
                    </div>
                    {isModuleDisabled(module.permission) && (
                      <UncontrolledTooltip
                        container="modules-table-container"
                        target={`check-m-${module.permission}`}
                        placement="top"
                      >
                        <FormattedMessage id="You don't have authorization to change this permission" />
                      </UncontrolledTooltip>
                    )}
                  </div>
                ))}
            </details>
          )
        })}
      </div>
    </StyledModules>
  )
}

const StyledModules = styled.div`
  width: 100%;

  .modules-table {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
  }

  details {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border-radius: 0.375rem;
    border: 1px solid #d1d1d1;

    .module-item {
      width: 100%;
      display: flex;
      flex-direction: row;
      justify-content: start;
      align-items: center;
      gap: 0.75rem;
      padding: 1rem 1.5rem;
      border-top: 1px solid #efefef;
      background-color: #f9f9f9;

      div:nth-child(1) {
        width: 100%;
      }
    }

    .module-item:last-child {
      border-radius: 0 0 0.375rem 0.375rem;
    }

    .group-closed {
      display: flex;
    }

    .group-open {
      display: none;
    }
  }

  details::details-content {
    width: 100%;
  }

  details[open] {
    .group-closed {
      display: none;
    }

    .group-open {
      display: flex;
    }
  }

  summary {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: start;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem 1.5rem;

    .group-name {
      width: 100%;
      color: #1a202c;
      font-weight: 500;
      font-size: 1rem;
      line-height: 1.5rem;
    }

    .group-chevron {
      min-width: 1.5rem;
    }
  }
`
